
import { defineComponent, ref, watch, inject } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAsyncTask } from 'vue-concurrency'
import { Dialog } from 'quasar'
import anime from 'animejs'
import api from '@/estimate/api'
import { ScrapForm, ScrapFormStepper } from '@/estimate/components'
import {
  ScrapInputKey,
  ScrapInput,
  ScrapImageKey,
  ScrapImage,
  ScrapValidationKey,
  ScrapValidation,
  ScrapIncentiveKey,
  ScrapIncentive,
} from '@/estimate/composables'

export default defineComponent({
  name: 'Form',
  setup() {
    const route = useRoute()
    const router = useRouter()

    /** 現在のSTEP */
    const currentStep = ref(Number(route.query.step) || 1)

    /** 入力データ */
    const { inputData, initInputData } = inject(ScrapInputKey) as ScrapInput
    /** 画像データ */
    const { initImageData } = inject(ScrapImageKey) as ScrapImage
    /** フォームの検証 */
    const { validate, errors, hasError, resetError } = inject(ScrapValidationKey) as ScrapValidation
    /** インセンティブ */
    const { totalIncentive, addAmount } = inject(ScrapIncentiveKey) as ScrapIncentive

    /** 表示用のインセンティブ合計金額（アニメーションのため） */
    const totalIncentiveForDisplay = ref(totalIncentive.value)

    const scrapFormRef = ref<typeof ScrapForm>()

    /** 査定申込タスク */
    const postOrderTask = useAsyncTask(async () => {
      return api.order
        .send(inputData)
        .then(() => {
          // 完了画面へ
          router.push('completed')
          // データをリセット
          initInputData()
          initImageData()
        })
        .catch(err => {
          // エラーダイアログを表示
          Dialog.create({ message: err })
        })
    })

    /** インセンティブ合計金額の変更を監視してアニメーション処理 */
    watch(totalIncentive, val => {
      anime({
        targets: totalIncentiveForDisplay,
        value: val,
        round: 1,
        duration: 430,
        easing: 'linear',
      })
    })

    /** 前のステップへ（戻る） */
    async function toBack() {
      try {
        // 一度エラー文言をリセット
        resetError()
        // 検証（必須項目）
        if (validate('index')) {
          throw Object.values(errors).filter(Boolean).join('<br>')
        }
        // 前のステップへ
        scrapFormRef.value?.prev()
        // スクロール位置を一番上へ
        window.scroll({ top: 0, behavior: 'smooth' })
      } catch (err) {
        // エラーダイアログを表示
        Dialog.create({ message: err, html: true })
      }
    }

    /** 次のステップへ（さらに入力で金額アップ） */
    async function toNext() {
      try {
        // 一度エラー文言をリセット
        resetError()
        // 検証（インセンティブ加算対象項目）
        if (validate(`step${currentStep.value}`)) {
          throw Object.values(errors).filter(Boolean).join('<br>')
        }
        // 次のステップへ
        scrapFormRef.value?.next()
        // スクロール位置を一番上へ
        window.scroll({ top: 0, behavior: 'smooth' })
      } catch (err) {
        // エラーダイアログを表示
        Dialog.create({ message: err, html: true })
      }
    }

    /** 今のステップを飛ばす（この項目をスキップ） */
    async function toSkip() {
      try {
        // 一度エラー文言をリセット
        resetError()
        // 検証（必須項目）
        if (validate('index')) {
          throw Object.values(errors).filter(Boolean).join('<br>')
        }
        if (currentStep.value === 7) {
          // 確認ダイアログ
          // onOk: 送信処理
          openConfirmDialog().onOk(postOrderTask.perform)
        } else {
          // 次のステップへ
          scrapFormRef.value?.next()
          // スクロール位置を一番上へ
          window.scroll({ top: 0, behavior: 'smooth' })
        }
      } catch (err) {
        // エラーダイアログを表示
        Dialog.create({ message: err, html: true })
      }
    }

    /** お見積り確認（ここまでの入力内容でお見積り依頼） */
    async function toConfirm() {
      try {
        // 検証（インセンティブ加算対象項目）
        if (validate(`step${currentStep.value}`)) {
          throw Object.values(errors).filter(Boolean).join('<br>')
        }
        // 確認ダイアログ
        // onOk: 送信処理
        openConfirmDialog().onOk(postOrderTask.perform)
      } catch (err) {
        Dialog.create({ message: err, html: true })
      }
    }

    /** 確認ダイアログを開く */
    function openConfirmDialog() {
      return Dialog.create({
        title: '見積り依頼確認',
        message: `<p>${inputData.userName}&emsp;様<br />ご連絡先「${inputData.userMob}」にて承ります。<br>買取金額は暫定${totalIncentive.value}円アップとなっております。</p><p>昭和メタルに見積りを依頼いたします。よろしいですか？</p>`,
        html: true,
        cancel: '入力し直す',
        ok: { label: '見積り依頼する', unelevated: true },
      })
    }

    return {
      // データ
      scrapFormRef,
      currentStep,
      hasError,
      addAmount,
      totalIncentive,
      totalIncentiveForDisplay,
      // 関数
      toBack,
      toNext,
      toSkip,
      toConfirm,
    }
  },
  components: { ScrapForm, ScrapFormStepper },
})
